
import { useState, useEffect } from 'react';

interface SendingAnimationProps {
  designId: number;
  text: string;
  recipient: string;
  onCreateNew: () => void;
}

const designStyles = {
  1: { bg: 'from-purple-900 via-pink-900 to-purple-900', textColor: 'text-pink-300' },
  2: { bg: 'from-pink-100 via-rose-100 to-pink-50', textColor: 'text-gray-800' },
  3: { bg: 'from-yellow-300 via-orange-300 to-pink-300', textColor: 'text-gray-900' },
  4: { bg: 'from-indigo-500 via-purple-500 to-pink-500', textColor: 'text-white' },
  5: { bg: 'from-blue-200 via-purple-200 to-pink-200', textColor: 'text-gray-800' }
};

export default function SendingAnimation({ designId, text, recipient, onCreateNew }: SendingAnimationProps) {
  const [stage, setStage] = useState<'sending' | 'sent'>('sending');
  const [showConfetti, setShowConfetti] = useState(false);

  // Add error handling for invalid designId
  const style = designStyles[designId as keyof typeof designStyles] || designStyles[1];

  useEffect(() => {
    // Анимация отправки 3 секунды
    const sendTimer = setTimeout(() => {
      setStage('sent');
      setShowConfetti(true);
    }, 3000);

    return () => clearTimeout(sendTimer);
  }, []);

  const handleShare = () => {
    try {
      if (navigator.share) {
        navigator.share({
          title: 'Моя Валентинка',
          text: 'Отправь валентинку своим друзьям в Telegram! 💌',
          url: window.location.href
        });
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <div className="animate-fade-in relative">
      {/* Confetti effect */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {[...Array(60)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10%',
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              <i className={`ri-heart-fill text-${['pink', 'rose', 'purple', 'red'][Math.floor(Math.random() * 4)]}-500 text-${[2, 3, 4][Math.floor(Math.random() * 3)]}xl`}></i>
            </div>
          ))}
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {stage === 'sending' ? (
          // Анимация отправки
          <div className="text-center">
            <div className="relative inline-block mb-8">
              {/* Валентинка в конверте */}
              <div className="relative animate-envelope-fly">
                <div className={`w-80 h-96 bg-gradient-to-br ${style.bg} rounded-3xl overflow-hidden shadow-2xl relative`}>
                  {/* Летающие сердечки */}
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute animate-heart-fly"
                      style={{
                        left: `${20 + Math.random() * 60}%`,
                        top: `${20 + Math.random() * 60}%`,
                        animationDelay: `${i * 0.2}s`
                      }}
                    >
                      <i className="ri-heart-fill text-pink-400 text-3xl"></i>
                    </div>
                  ))}

                  {/* Текст валентинки */}
                  <div className="absolute inset-0 flex items-center justify-center p-8 z-10">
                    <p className={`text-xl font-medium text-center leading-relaxed ${style.textColor}`}>
                      {text}
                    </p>
                  </div>
                </div>

                {/* Конверт */}
                <div className="absolute inset-0 animate-envelope-close">
                  <div className="absolute inset-0 bg-gradient-to-br from-rose-400 to-pink-500 rounded-3xl opacity-80"></div>
                  <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-br from-rose-500 to-pink-600 rounded-t-3xl transform origin-top animate-envelope-flap"></div>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold mb-4 text-white">
              Отправляем валентинку...
            </h2>
            <p className="text-lg text-gray-400">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Создаём магию для @{recipient}
            </p>
          </div>
        ) : (
          // Экран успешной отправки
          <div className="text-center">
            <div className="inline-block mb-8 animate-bounce-in">
              <div className="w-32 h-32 bg-gradient-to-br from-[#2AABEE] to-[#229ED9] rounded-full flex items-center justify-center shadow-2xl">
                <i className="ri-check-line text-white text-6xl"></i>
              </div>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Валентинка отправлена!
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              💌 Вам придет уведомление, когда получатель откроет валентинку
            </p>

            {/* Информация о получателе */}
            <div className="bg-[#232E3C] rounded-3xl p-8 mb-8 border border-[#2C3847]">
              <div className="flex items-center justify-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center">
                  <i className="ri-user-heart-fill text-white text-3xl"></i>
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-500 mb-1">Получатель</p>
                  <p className="text-2xl font-bold text-white">@{recipient}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#17212b] rounded-2xl p-4 border border-[#2C3847]">
                  <i className="ri-notification-3-fill text-[#2AABEE] text-3xl mb-2"></i>
                  <p className="text-sm text-gray-400">Уведомление отправлено</p>
                </div>
                <div className="bg-[#17212b] rounded-2xl p-4 border border-[#2C3847]">
                  <i className="ri-time-fill text-[#2AABEE] text-3xl mb-2"></i>
                  <p className="text-sm text-gray-400">Доставка &lt;1 мин</p>
                </div>
              </div>
            </div>

            {/* Кнопки действий */}
            <div className="space-y-4">
              <button
                onClick={onCreateNew}
                className="w-full py-5 bg-gradient-to-r from-[#2AABEE] to-[#229ED9] text-white rounded-2xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-circle-fill text-2xl"></i>
                Создать ещё одну валентинку
              </button>

              <button
                onClick={handleShare}
                className="w-full py-5 bg-[#232E3C] text-white rounded-2xl font-bold text-lg hover:bg-[#2C3847] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer border border-[#2C3847]"
              >
                <i className="ri-share-fill text-2xl"></i>
                Поделиться сервисом
              </button>
            </div>

            {/* Статистика */}
            <div className="mt-8 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-8 text-white">
              <h3 className="text-xl font-bold mb-6 flex items-center justify-center gap-2">
                <i className="ri-fire-fill text-2xl"></i>
                Популярность сервиса
              </h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-4xl font-bold">12K+</p>
                  <p className="text-sm text-purple-100 mt-2">Валентинок</p>
                </div>
                <div>
                  <p className="text-4xl font-bold">6K+</p>
                  <p className="text-sm text-purple-100 mt-2">Пользователей</p>
                </div>
                <div>
                  <p className="text-4xl font-bold">4.9★</p>
                  <p className="text-sm text-purple-100 mt-2">Рейтинг</p>
                </div>
              </div>
            </div>

            {/* Бонус */}
            <div className="mt-8">
              <div className="inline-block bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400 rounded-full p-1 shadow-2xl animate-pulse">
                <div className="bg-[#232E3C] rounded-full px-8 py-4">
                  <p className="text-lg font-bold text-white">
                    🎁 Отправь ещё 2 валентинки — получи эксклюзивный дизайн!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes heart-fly {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(var(--tx, 100px), var(--ty, -200px)) scale(0); opacity: 0; }
        }
        @keyframes envelope-fly {
          0% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
          100% { transform: translateY(-100vh) scale(0.5); opacity: 0; }
        }
        @keyframes envelope-close {
          0% { opacity: 0; }
          30% { opacity: 1; }
          100% { opacity: 1; }
        }
        @keyframes envelope-flap {
          0% { transform: rotateX(0deg); }
          50% { transform: rotateX(180deg); }
          100% { transform: rotateX(180deg); }
        }
        @keyframes bounce-in {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
        .animate-heart-fly {
          animation: heart-fly 2s ease-out forwards;
          --tx: calc((Math.random() - 0.5) * 400px);
          --ty: calc(-200px - Math.random() * 200px);
        }
        .animate-envelope-fly {
          animation: envelope-fly 3s ease-in forwards;
        }
        .animate-envelope-close {
          animation: envelope-close 1.5s ease-out forwards;
        }
        .animate-envelope-flap {
          animation: envelope-flap 1.5s ease-out forwards;
        }
        .animate-bounce-in {
          animation: bounce-in 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
      `}</style>
    </div>
  );
}
